import { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
    useLoopBestQuote,
    LoopQuoteExpanded,
    getLoopSlippage,
    LoopExpanded,
    PriceFetchType,
    useLoopscaleWalletContext,
    useUnderlyingTokenInfo,
    usePandoraQuoteAndIxs,
    useMarketsQuoteAndIxs,
    useBestQuotes,
    useMaxQuotesForCollateral,
    BestQuote,
    MaxQuoteFromCollateralAmount,
    getCollateralMintToInfoFromForm,
    Collateral,
    isLoopPrincipalWindDisabled,
    isLoopCollateralWindDisabled
} from "@bridgesplit/abf-react";
import { DispatchType } from "@bridgesplit/react";
import { percentUiToDecimals, roundDownToDecimals, uiAmountToLamports } from "@bridgesplit/utils";
import { DEFAULT_SLIPPAGE } from "app/components/common";
import { LoopRouteType, OracleQuote, TokenListMetadata, VaultMintLpParams } from "@bridgesplit/abf-sdk";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { SwapParams, SwapInstructionsResponse } from "@loopscale/pandora-types";

import { WindForm, LoopMode } from "./types";
import { useLoopContext } from "../LoopContext";
import { DEFAULT_WIND_FORM } from "./constants";
import { LoopWinderFactory } from "./winders";
import { LoopWinder } from "./winders/types";

interface WindContext {
    quote: LoopQuoteExpanded | null | undefined;
    externalQuote: SwapInstructionsResponse | undefined;
    externalQuoteLoading: boolean;
    externalQuoteError: FetchBaseQueryError | SerializedError | undefined;
    collateralAmount: number | undefined; // this is the amount of collateral being deposited into the loop. for most loops this is the same as input amount, but for orca loops it is 1
    leveragedCollateralAmountToDeposit: number | undefined; // this is the amount of collateral being deposited into the loop. for most loops this is the same as input amount, but for orca loops it is 1
    inputToken: TokenListMetadata | undefined; // the token used to start the loop. populates the loop_vault_deposits_v1 table
    isQuoteFetching: boolean;
    isQuoteLoading: boolean;
    loopMode: LoopMode;
    setLoopMode: DispatchType<LoopMode>;
    form: WindForm;
    setForm: DispatchType<WindForm>;
    isActionOpen: boolean;
    setIsActionOpen: DispatchType<boolean>;
    supportedDepositTokens: TokenListMetadata[] | undefined;
    oraclePrices?: Map<string, OracleQuote | undefined>;
    LoopWinder: LoopWinder;
    isInputDisabled: boolean;
    isMultiplierDisabled: boolean;
    // Manual mode borrow quotes (same as BorrowCTA)
    bestBorrowQuote: BestQuote | undefined;
    maxBorrowQuote: MaxQuoteFromCollateralAmount | null | undefined;
    borrowQuoteFetching: boolean;
}
// empty commit comment after rebase - only thing that changes was rebasing off of staging with new isMultiplierDisabled

const MAX_BORROW_SAFETY_FACTOR = 0.99;

const LoopContext = createContext<WindContext | null>(null);

export function useWindContext(): WindContext {
    const context = useContext(LoopContext);
    if (context === null) {
        throw new Error("useWindContext must be used within a LoopProvider");
    }
    return context;
}

export function WindProvider({ children }: { children: React.ReactNode }) {
    const { loopExpanded } = useLoopContext();
    const [isActionOpen, setIsActionOpen] = useState(false);
    const [loopMode, setLoopMode] = useState<LoopMode>(LoopMode.Auto);
    const [form, setForm] = useState<WindForm>(DEFAULT_WIND_FORM);
    const {
        data: { userWalletAddress: user }
    } = useLoopscaleWalletContext();
    const { oraclePrices } = useUnderlyingTokenInfo(
        loopExpanded ? [loopExpanded.collateralToken.mint, loopExpanded.principalToken.mint] : []
    );

    const LoopWinder = useMemo(() => {
        const winder = LoopWinderFactory.create(
            loopExpanded?.loopVault.routeType ?? undefined,
            form.inputToken,
            loopExpanded?.principalToken.mint
        );

        return winder;
    }, [loopExpanded?.loopVault.routeType, form.inputToken, loopExpanded?.principalToken.mint]);

    const defaultSlippageDecimals = percentUiToDecimals(getLoopSlippage(loopExpanded).defaultSlippageUi);

    useEffect(() => {
        if (loopExpanded && form.percentSlippageDecimals === undefined) {
            setForm({
                ...form,
                percentSlippageDecimals: defaultSlippageDecimals
            });
        }
    }, [defaultSlippageDecimals, form, loopExpanded]);

    // Add effect to set Orca position params
    // TODO: would be nice to get rid of this

    useEffect(() => {
        const defaultInputToken = getDefaultWindInputToken(loopExpanded);
        setForm((prevForm) => ({
            ...prevForm,
            inputToken: defaultInputToken
        }));
    }, [loopExpanded]);

    const priceOverride = useMemo(() => {
        return LoopWinder.getPriceOverride({ loopExpanded, inputAmount: form.inputAmount });
    }, [form.inputAmount, LoopWinder, loopExpanded]);

    const principalWindQuoteParams = useMemo(() => {
        return LoopWinder.buildPandoraPrincipalWindQuoteParams({
            loopExpanded,
            form,
            userWallet: user
        });
    }, [loopExpanded, form, user, LoopWinder]);

    const principalWindQuote = usePandoraQuoteAndIxs(
        loopMode === LoopMode.Manual ? undefined : principalWindQuoteParams
    );

    const collateralAmount = LoopWinder.getCollateralInputAmountForQuote({
        loopExpanded,
        form,
        principalWindExternalQuote: principalWindQuote.data
    });

    // For Manual mode, calculate leverage from collateral and principal amounts
    const leverageMultiplier = useMemo(() => {
        if (loopMode === LoopMode.Manual && form.collateralAmount && form.principalAmount && oraclePrices) {
            const collateralOracle = oraclePrices.get(loopExpanded?.collateralToken.mint || "");
            const principalOracle = oraclePrices.get(loopExpanded?.principalToken.mint || "");

            if (collateralOracle && principalOracle) {
                const collateralUsd = collateralOracle.getUsdAmount(
                    form.collateralAmount,
                    loopExpanded?.collateralToken.decimals
                );
                const principalUsd = principalOracle.getUsdAmount(
                    form.principalAmount,
                    loopExpanded?.principalToken.decimals
                );

                if (collateralUsd && principalUsd) {
                    // Leverage = (Collateral + Borrowed) / Collateral
                    const leverage = (collateralUsd + principalUsd) / collateralUsd;
                    return leverage - (form.percentSlippageDecimals ?? DEFAULT_SLIPPAGE);
                }
            }
        }

        // For auto mode, use form.multiplier
        return form.multiplier ? form.multiplier - (form.percentSlippageDecimals ?? DEFAULT_SLIPPAGE) : undefined;
    }, [
        loopMode,
        form.collateralAmount,
        form.principalAmount,
        form.multiplier,
        form.percentSlippageDecimals,
        oraclePrices,
        loopExpanded?.collateralToken,
        loopExpanded?.principalToken
    ]);

    const loopBestQuote = useLoopBestQuote({
        loopExpanded: loopExpanded,
        leverageMultiplier,
        collateralAmountUi: loopMode === LoopMode.Manual ? form.collateralAmount : collateralAmount,
        inputAmountUi: loopMode === LoopMode.Manual ? form.collateralAmount : form.inputAmount,
        strategyDuration: form.strategyDuration,
        priceOverride,
        oracleQuotes: oraclePrices,
        inputToken: form.inputToken
    });

    const settledMultiplier = useMemo(() => {
        if (form.multiplierWithoutDebounce === undefined) return undefined;
        return Math.min(
            Math.floor(form.multiplierWithoutDebounce * 10) / 10,
            Math.floor((loopExpanded?.loopVault.maxLeverage ?? 1) * 10) / 10
        );
    }, [form.multiplierWithoutDebounce, loopExpanded?.loopVault.maxLeverage]);

    const isMultiplierPending = settledMultiplier !== undefined && settledMultiplier !== form.multiplier;
    const quote = isMultiplierPending ? undefined : loopBestQuote.data;

    const externalQuote = useWindQuote({
        loopExpanded,
        user,
        LoopWinder,
        loopBestQuote: quote,
        form,
        loopMode
    });

    const leveragedCollateralAmountToDeposit = useMemo(() => {
        return LoopWinder.getLeveragedCollateralAmountToDeposit({
            loopExpanded,
            form,
            externalQuote: externalQuote.data
        });
    }, [form, externalQuote.data, LoopWinder, loopExpanded]);

    const isQuoteFetching = isMultiplierPending || loopBestQuote.isFetching || externalQuote.isFetching;
    const isQuoteLoading = isMultiplierPending || loopBestQuote.isLoading || externalQuote.isLoading;
    const isInputDisabled = !loopExpanded || loopExpanded.loopVault.routeType === LoopRouteType.Manual;
    const isMultiplierDisabled = isInputDisabled || !form.inputAmount;

    const supportedDepositTokens = useMemo(() => {
        if (!loopExpanded) return undefined;

        const principalMint = loopExpanded.principalToken.mint;
        const collateralMint = loopExpanded.collateralToken.mint;
        const disablePrincipal =
            isLoopPrincipalWindDisabled(loopExpanded.loopVault) && principalMint !== collateralMint;
        const disableCollateral =
            isLoopCollateralWindDisabled(loopExpanded.loopVault) && principalMint !== collateralMint;
        const uniqueTokens = new Map<string, TokenListMetadata>();

        [loopExpanded.principalToken, loopExpanded.collateralToken].forEach((token) => {
            if (disablePrincipal && token.mint === principalMint) return;
            if (disableCollateral && token.mint === collateralMint) return;
            uniqueTokens.set(token.mint, token);
        });

        return Array.from(uniqueTokens.values());
    }, [loopExpanded]);

    // Manual mode: Build proper Collateral object for quote hooks (same as BorrowCTA)
    const manualCollateral: Collateral | undefined = useMemo(() => {
        if (loopMode !== LoopMode.Manual || !form.collateralAmount || !loopExpanded) {
            return undefined;
        }

        const collateralOracle = oraclePrices?.get(loopExpanded.collateralToken.mint);
        const usdPrice = collateralOracle?.usdPrice ?? 0;
        const usdValue =
            collateralOracle?.getUsdAmount(form.collateralAmount, loopExpanded.collateralToken.decimals) ?? 0;

        return {
            key: loopExpanded.collateralToken.mint,
            metadata: loopExpanded.collateralToken,
            amount: form.collateralAmount,
            oracle: collateralOracle ?? null,
            usdPrice,
            usdValue
        };
    }, [loopMode, form.collateralAmount, loopExpanded, oraclePrices]);

    const collateralMintToInfo = useMemo(() => {
        if (!manualCollateral) return undefined;
        return getCollateralMintToInfoFromForm(
            manualCollateral.metadata.mint,
            manualCollateral,
            manualCollateral.amount
        );
    }, [manualCollateral]);

    // For manual mode, get best quotes for the borrow (same as BorrowCTA)
    const { data: borrowQuotes, isFetching: borrowQuoteFetching } = useBestQuotes({
        collateralMintToInfo,
        principalMint: loopExpanded?.principalToken.mint,
        minPrincipalAmountLamports:
            form.principalAmount && loopExpanded?.principalToken.decimals
                ? uiAmountToLamports(form.principalAmount, loopExpanded.principalToken.decimals)
                : undefined,
        strategyDurations: form.strategyDuration ? [form.strategyDuration] : undefined,
        skip: loopMode !== LoopMode.Manual || !form.principalAmount
    });

    // Get max quote for collateral (same as BorrowCTA)
    const maxQuotes = useMaxQuotesForCollateral({
        collateral: manualCollateral ? [manualCollateral] : [],
        strategyDuration: form.strategyDuration,
        principalMint: loopExpanded?.principalToken.mint,
        priceFetchType: PriceFetchType.Twap
    });

    const rawMaxBorrowQuote = useMemo(() => {
        if (loopMode !== LoopMode.Manual || maxQuotes.isLoading || !maxQuotes.data || maxQuotes.isFetching) {
            return undefined;
        }

        const collateralMint = manualCollateral?.metadata.mint ?? loopExpanded?.collateralToken.mint;
        return maxQuotes.data.find((c) => c.metadata.mint === collateralMint)?.maxQuote ?? null;
    }, [loopMode, maxQuotes, manualCollateral?.metadata.mint, loopExpanded?.collateralToken.mint]);

    const { data: bestQuotesAtMax } = useBestQuotes({
        collateralMintToInfo,
        principalMint: loopExpanded?.principalToken.mint,
        minPrincipalAmountLamports:
            rawMaxBorrowQuote?.maxBorrow !== undefined && loopExpanded?.principalToken.decimals !== undefined
                ? uiAmountToLamports(rawMaxBorrowQuote.maxBorrow, loopExpanded.principalToken.decimals)
                : undefined,
        strategyDurations: form.strategyDuration ? [form.strategyDuration] : undefined,
        skip:
            loopMode !== LoopMode.Manual ||
            !collateralMintToInfo ||
            rawMaxBorrowQuote?.maxBorrow === undefined ||
            !form.strategyDuration
    });

    const maxBorrowQuote = useMemo(() => {
        if (!rawMaxBorrowQuote) return rawMaxBorrowQuote;
        if (!bestQuotesAtMax || !form.strategyDuration) return rawMaxBorrowQuote;

        const bestQuoteAtMax = bestQuotesAtMax.find(
            (q) =>
                q.duration === form.strategyDuration?.duration && q.durationType === form.strategyDuration?.durationType
        );
        if (!bestQuoteAtMax) {
            // Empty means the spot-priced raw max is over the backend's TWAP-priced ceiling; shave it too.
            return {
                ...rawMaxBorrowQuote,
                maxBorrow:
                    roundDownToDecimals(rawMaxBorrowQuote.maxBorrow, loopExpanded?.principalToken.decimals) *
                    MAX_BORROW_SAFETY_FACTOR
            };
        }

        const principalUsdPrice = oraclePrices?.get(loopExpanded?.principalToken.mint ?? "")?.usdPrice;
        const collateralUsdValue = manualCollateral?.usdValue;
        const maxBorrowFromBestLtv =
            principalUsdPrice && collateralUsdValue
                ? (collateralUsdValue * bestQuoteAtMax.ltv) / principalUsdPrice
                : undefined;

        const effectiveMaxBorrow = Math.min(
            bestQuoteAtMax.principalAvailable,
            maxBorrowFromBestLtv ?? Number.POSITIVE_INFINITY
        );

        const safeMaxBorrow =
            roundDownToDecimals(effectiveMaxBorrow, loopExpanded?.principalToken.decimals) * MAX_BORROW_SAFETY_FACTOR;

        return {
            ...rawMaxBorrowQuote,
            ltv: bestQuoteAtMax.ltv,
            maxBorrow: safeMaxBorrow,
            principalAvailable: bestQuoteAtMax.principalAvailable
        };
    }, [
        rawMaxBorrowQuote,
        bestQuotesAtMax,
        form.strategyDuration,
        oraclePrices,
        loopExpanded?.principalToken.mint,
        manualCollateral?.usdValue,
        loopExpanded?.principalToken.decimals
    ]);

    // Best quote with matching duration and sufficient principal
    const bestBorrowQuote = useMemo(() => {
        if (loopMode !== LoopMode.Manual || !borrowQuotes || !form.strategyDuration) {
            return undefined;
        }
        // Filter for quotes with sufficient principal and matching duration
        const availableQuotes = borrowQuotes.filter((q) => {
            const hasSufficientPrincipal = form.principalAmount ? q.principalAvailable >= form.principalAmount : true;
            const hasMatchingDuration =
                q.duration === form.strategyDuration?.duration &&
                q.durationType === form.strategyDuration?.durationType;
            return hasSufficientPrincipal && hasMatchingDuration;
        });

        // Return the quote with best LTV
        return availableQuotes.reduce((best, current) => {
            if (!best || current.ltv > best.ltv) return current;
            return best;
        }, undefined as BestQuote | undefined);
    }, [loopMode, borrowQuotes, form.principalAmount, form.strategyDuration]);

    return (
        <LoopContext.Provider
            value={{
                quote,
                inputToken: loopExpanded?.inputToken,
                loopMode,
                setLoopMode,
                form,
                setForm,
                externalQuote: externalQuote.data,
                externalQuoteLoading: isMultiplierPending || externalQuote.isFetching || externalQuote.isLoading,
                leveragedCollateralAmountToDeposit,
                collateralAmount,
                isQuoteFetching,
                isQuoteLoading,
                isActionOpen,
                setIsActionOpen,
                supportedDepositTokens,
                oraclePrices,
                externalQuoteError: isQuoteFetching ? undefined : externalQuote.error,
                LoopWinder,
                isInputDisabled,
                isMultiplierDisabled,
                bestBorrowQuote,
                maxBorrowQuote,
                borrowQuoteFetching
            }}
        >
            {children}
        </LoopContext.Provider>
    );
}

function getDefaultWindInputToken(loopExpanded: LoopExpanded | undefined): TokenListMetadata | undefined {
    if (!loopExpanded) return undefined;

    const principalMint = loopExpanded.principalToken.mint;
    const collateralMint = loopExpanded.collateralToken.mint;
    if (principalMint === collateralMint) return loopExpanded.inputToken;

    if (isLoopCollateralWindDisabled(loopExpanded.loopVault)) return loopExpanded.principalToken;
    if (isLoopPrincipalWindDisabled(loopExpanded.loopVault)) return loopExpanded.collateralToken;

    return loopExpanded.inputToken;
}

interface UseWindQuoteParams {
    loopExpanded: LoopExpanded | undefined;
    form: WindForm;
    user: string | undefined;
    LoopWinder: LoopWinder;
    loopBestQuote: LoopQuoteExpanded | null | undefined;
    loopMode: LoopMode;
}

// winder will only have a defined response for Either Pandora or Markets.
// Hooks can't run conditionally. Undefined passed to the query hooks will skip the query.
function useWindQuote({ loopExpanded, form, user, LoopWinder, loopBestQuote, loopMode }: UseWindQuoteParams) {
    const windRouteParams = useMemo(
        () =>
            loopMode === LoopMode.Manual
                ? undefined
                : LoopWinder.buildWindQuoteParams({
                      loopExpanded,
                      form,
                      userWallet: user,
                      quote: loopBestQuote
                  }),
        [loopExpanded, form, user, LoopWinder, loopBestQuote, loopMode]
    );

    const isPandora = LoopWinder.windQuoteTarget === "pandora";

    // Only pass params to the active hook, undefined skips the other
    const pandoraWindQuote = usePandoraQuoteAndIxs(isPandora ? (windRouteParams as SwapParams | undefined) : undefined);
    const marketWindQuote = useMarketsQuoteAndIxs(
        isPandora ? undefined : (windRouteParams as VaultMintLpParams | undefined)
    );

    const activeWindQuote = isPandora ? pandoraWindQuote : marketWindQuote;

    return {
        ...activeWindQuote,
        data: activeWindQuote.currentData
    };
}
